import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import ScrollReveal from "../components/ScrollReveal";
import CTASection from "../components/sections/CTASection";
import { SERVICES, CALENDLY_URL } from "../data/content";

export default function ServicesPage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [hash]);

  return (
    <div data-testid="services-page">
      {/* Page hero */}
      <section className="pt-36 md:pt-44 pb-16 md:pb-24 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <ScrollReveal>
            <div className="text-xs uppercase tracking-[0.4em] text-white/40 mb-4">
              / Services
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-black uppercase tracking-tighter leading-[0.95]">
              What we<br /><span className="text-white/40">forge for you.</span>
            </h1>
            <p className="mt-6 text-white/60 max-w-2xl text-base md:text-lg leading-relaxed">
              Six sharp services — each designed to turn attention into
              revenue. Pick one, stack them, or book a call to architect a
              custom engagement.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Service blocks */}
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          return (
            <section
              key={s.slug}
              id={s.slug}
              data-testid={`service-detail-${s.slug}`}
              className={`py-20 md:py-28 border-b border-white/10 grid lg:grid-cols-2 gap-10 lg:gap-20 items-center ${
                i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <ScrollReveal>
                <div className="w-14 h-14 border border-white/20 flex items-center justify-center mb-6">
                  <Icon size={22} strokeWidth={1.4} />
                </div>
                <div className="text-xs uppercase tracking-[0.3em] text-white/40 mb-3">
                  {String(i + 1).padStart(2, "0")} / {s.title}
                </div>
                <h2 className="font-display text-3xl md:text-5xl font-black uppercase tracking-tight leading-[1]">
                  {s.title}
                </h2>
                <div className="mt-4 font-display text-2xl md:text-3xl font-black text-white/70">
                  {s.price}
                </div>
                <p className="mt-5 text-white/60 text-base md:text-lg leading-relaxed max-w-lg">
                  {s.description}
                </p>
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`service-cta-${s.slug}`}
                  className="mt-7 inline-flex items-center gap-2 btn-violet px-5 py-3 text-xs uppercase tracking-[0.25em] font-semibold"
                >
                  Start This Project <ArrowUpRight size={14} />
                </a>
              </ScrollReveal>

              <ScrollReveal delay={0.1}>
                <div className="border border-white/10 bg-[#080808] p-8 md:p-10">
                  <div className="text-xs uppercase tracking-[0.3em] text-white/40 mb-5">
                    / What you get
                  </div>
                  <ul className="space-y-4">
                    {s.deliverables.map((d) => (
                      <li key={d} className="flex items-start gap-3 text-white/80">
                        <Check size={16} className="mt-1 text-white shrink-0" strokeWidth={2} />
                        <span className="text-sm md:text-base leading-relaxed">{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>
            </section>
          );
        })}
      </div>

      <CTASection />
    </div>
  );
}
